import type {
  Kautionsvertrag,
  KautionsvertragListItem,
  KautionsvertragStatus,
  KautionsvertragUpdateDto,
} from '@/types/Vertrag.ts';
import { SharedKreditvertragService } from '@/shared/api/services/partner/kreditvertrag.ts';
import type { KautionsvertragErstellenDto } from '@/auftraggeber/components/vertraege/types.ts';

export class KreditvertragService extends SharedKreditvertragService {
  async erstelleKautionsvertrag(dto: KautionsvertragErstellenDto): Promise<Kautionsvertrag> {
    return this.httpClient.post(`${this.baseUrl}`, {
      data: dto,
    });
  }

  async bearbeiteWeitereUnternehmenAmKautionsvertrag(
    kautionsvertragId: string,
    weitereUnternehmenIds: string[],
    versicherungsnehmerId?: string,
  ): Promise<Kautionsvertrag> {
    return this.httpClient.post(`${this.baseUrl}/${kautionsvertragId}`, {
      data: weitereUnternehmenIds,
      params: { versicherungsnehmerId },
    });
  }

  async deleteKautionsvertrag(kautionsvertragId: string): Promise<Kautionsvertrag> {
    return this.httpClient.delete(`${this.baseUrl}/${kautionsvertragId}`);
  }

  async getLinkedAvallineId(kautionsvertragId: string): Promise<number> {
    return this.httpClient
      .get(`${this.baseUrl}/${kautionsvertragId}/avalinie`)
      .then((value) => (value === '' ? null : (value as number)));
  }

  async bearbeiteKautionsvertrag(
    vertragsdaten: KautionsvertragUpdateDto,
  ): Promise<Kautionsvertrag> {
    return this.httpClient.post(`${this.baseUrl}/bearbeiten`, {
      data: vertragsdaten,
    });
  }

  async holeKautionsvertraege(
    vertragsnummer: string,
    buergeId: string,
    versicherungsnehmerId?: string,
  ): Promise<Kautionsvertrag[]> {
    return this.httpClient.get(`${this.baseUrl}/buerge/${buergeId}`, {
      params: { vertragsnummer, versicherungsnehmerId },
    });
  }

  async holeKautionsvertraegeZuBuergeUndStatus(
    buergeId: string,
    status?: KautionsvertragStatus,
  ): Promise<KautionsvertragListItem[]> {
    return this.httpClient.get(`${this.baseUrl}`, {
      params: { buergeId, status },
    });
  }

  /**
   * Lädt eine Liste von Kautionsverträgen zu einem bestimmten Bürgen,
   * die für eine neue Avallinie in Frage kommen.
   *
   * Filter:
   * - Verträge mit Status "abgelehnt" werden ausgeschlossen
   * - Verträge, die bereits einer Avallinie zugeordnet sind, werden ausgeschlossen
   *   (außer es handelt sich um die aktuell bearbeitete Avallinie, falls `avallinieId` übergeben wird)
   *
   * @param buergeId      Die ID des Bürgen, für den die Verträge abgefragt werden sollen
   * @param avallinieId   Optional: ID der Avallinie, deren Verträge trotzdem berücksichtigt werden dürfen
   * @returns Promise mit den verfügbaren Kautionsverträgen
   */
  async holeKautionsvertraegeZuBuergeOhneAbgelehnte(
    buergeId: string,
    avallinieId?: number,
  ): Promise<KautionsvertragListItem[]> {
    return this.httpClient.get(`${this.baseUrl}/avallinienfrei`, {
      params: {
        buergeId,
        avallinieId,
      },
    });
  }

  async holeBuergeIdsMitVertragFuerAuftragnehmer(avallinieId?: number): Promise<string[]> {
    return this.httpClient.get(`${this.baseUrl}/buergen-ids`, {
      params: {
        avallinieId,
      },
    });
  }
}
